import { hasCoupons } from '@wix/ambassador-coupons-api-v1-coupon/http';
import {
  memberGetOrder,
  getOnlineOrderPreview,
  createOnlineOrder,
  changeStartDate,
  getPricePreview,
  applyCoupon,
  createGuestOnlineOrder,
  setSubmission,
} from '@wix/ambassador-pricing-plans-v2-order/http';
import { GetOnlineOrderPreviewResponse, Order, SpannedPrice } from '@wix/ambassador-pricing-plans-v2-order/types';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { ControllerFlowAPI, IHttpClient } from '@wix/yoshi-flow-editor';
import {
  getPricingPlansV2OrderErrorCodesMap,
  PricingPlansV2OrderErrorCodesMap,
} from './error-code-maps/pricing-plans-v2-order';

type ErrorHandler = ControllerFlowAPI['errorHandler'];

export interface CreateGuestOrderParams {
  email: string;
  captchaToken: string;
  planId: string;
  submissionId?: string;
}

export interface OrdersApi {
  getOrder(id: string): Promise<Order | undefined>;
  createOrderPreview(planId: string): Promise<GetOnlineOrderPreviewResponse>;
  createOrder(planId: string, submissionId?: string): Promise<Order | undefined>;
  updateOrderValidFrom(orderId: string, validFrom: Date): Promise<Order | undefined>;
  getPricePreview(planId: string, couponCode?: string): Promise<SpannedPrice[] | undefined>;
  applyCoupon(orderId: string, couponCode: string): Promise<Order | undefined>;
  setSubmission(orderId: string, submissionId: string): Promise<Order | undefined>;
  hasCoupons(): Promise<boolean>;
  createGuestOrder({ planId, email, captchaToken, submissionId }: CreateGuestOrderParams): Promise<Order | undefined>;
}

class OrdersService implements OrdersApi {
  constructor(protected httpClient: IHttpClient) {}

  async getOrder(id: string) {
    const response = await this.httpClient.request(memberGetOrder({ id }));
    const { order } = response.data;
    return order;
  }

  public async createOrderPreview(planId: string) {
    const response = await this.httpClient.request(getOnlineOrderPreview({ planId, startDate: new Date() }));
    return response.data;
  }

  public async createOrder(planId: string, submissionId?: string) {
    const response = await this.httpClient.request(
      createOnlineOrder(submissionId ? { planId, submissionId } : { planId }),
    );
    const { order } = response.data;
    return order;
  }

  public async updateOrderValidFrom(orderId: string, validFrom: Date) {
    const response = await this.httpClient.request(
      changeStartDate({
        orderId,
        startDate: validFrom,
      }),
    );
    const { order } = response.data;
    return order;
  }

  public async getPricePreview(planId: string, couponCode?: string) {
    const response = await this.httpClient.request(getPricePreview(couponCode ? { planId, couponCode } : { planId }));
    const { prices } = response.data;
    return prices;
  }

  public async applyCoupon(orderId: string, couponCode: string) {
    const response = await this.httpClient.request(applyCoupon({ orderId, couponCode }));
    const { order } = response.data;
    return order;
  }

  public async setSubmission(orderId: string, submissionId: string) {
    const response = await this.httpClient.request(setSubmission({ orderId, submissionId }));
    const { order } = response.data;
    return order;
  }

  public async hasCoupons() {
    try {
      const response = await this.httpClient.request(hasCoupons({}));
      return Boolean(response.data.hasCoupons);
    } catch (e) {
      // @todo: do we need to log error here?
      return false;
    }
  }

  public async createGuestOrder({ planId, email, captchaToken, submissionId }: CreateGuestOrderParams) {
    const response = await this.httpClient.request(
      createGuestOnlineOrder(
        submissionId
          ? { planId, guest: { email }, captcha: { token: captchaToken }, submissionId }
          : { planId, guest: { email }, captcha: { token: captchaToken } },
      ),
    );

    return response.data.order;
  }
}

class OrdersServiceWithErrorHandler implements OrdersApi {
  constructor(
    protected httpClient: IHttpClient,
    private errorHandler: ErrorHandler,
    private errorCodesMap: PricingPlansV2OrderErrorCodesMap,
  ) {}

  async getOrder(id: string) {
    const response = await this.errorHandler.withErrorHandler(
      () => this.httpClient.request(memberGetOrder({ id })),
      this.errorCodesMap,
    );
    const { order } = response.data;
    return order;
  }

  public async createOrderPreview(planId: string) {
    const response = await this.errorHandler.withErrorHandler(
      () => this.httpClient.request(getOnlineOrderPreview({ planId, startDate: new Date() })),
      this.errorCodesMap,
    );
    return response.data;
  }

  public async createOrder(planId: string, submissionId?: string) {
    const response = await this.errorHandler.withErrorHandler(
      () => this.httpClient.request(createOnlineOrder(submissionId ? { planId, submissionId } : { planId })),
      this.errorCodesMap,
    );
    const { order } = response.data;
    return order;
  }

  public async updateOrderValidFrom(orderId: string, validFrom: Date) {
    const response = await this.errorHandler.withErrorHandler(
      () =>
        this.httpClient.request(
          changeStartDate({
            orderId,
            startDate: validFrom,
          }),
        ),
      this.errorCodesMap,
    );
    const { order } = response.data;
    return order;
  }

  public async getPricePreview(planId: string, couponCode?: string) {
    const response = await this.errorHandler.withErrorHandler(
      () => this.httpClient.request(getPricePreview(couponCode ? { planId, couponCode } : { planId })),
      this.errorCodesMap,
    );
    const { prices } = response.data;
    return prices;
  }

  public async applyCoupon(orderId: string, couponCode: string) {
    const response = await this.errorHandler.withErrorHandler(
      () => this.httpClient.request(applyCoupon({ orderId, couponCode })),
      this.errorCodesMap,
    );
    const { order } = response.data;
    return order;
  }

  public async setSubmission(orderId: string, submissionId: string) {
    const response = await this.errorHandler.withErrorHandler(
      () => this.httpClient.request(setSubmission({ orderId, submissionId })),
      this.errorCodesMap,
    );
    const { order } = response.data;
    return order;
  }

  public async hasCoupons() {
    try {
      const response = await this.errorHandler.withErrorHandler(
        () => this.httpClient.request(hasCoupons({})),
        this.errorCodesMap,
      );
      return Boolean(response.data.hasCoupons);
    } catch (e) {
      // @todo: do we need to log error here?
      return false;
    }
  }

  public async createGuestOrder({ planId, email, captchaToken, submissionId }: CreateGuestOrderParams) {
    const response = await this.errorHandler.withErrorHandler(
      () =>
        this.httpClient.request(
          createGuestOnlineOrder(
            submissionId
              ? { planId, guest: { email }, captcha: { token: captchaToken }, submissionId }
              : { planId, guest: { email }, captcha: { token: captchaToken } },
          ),
        ),
      this.errorCodesMap,
    );

    return response.data.order;
  }
}

export function createOrdersApi(flowAPI: ControllerFlowAPI) {
  const isErrorHandlerEnabled = flowAPI.experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA);
  const errorCodesMap = getPricingPlansV2OrderErrorCodesMap(flowAPI.translations.t);

  if (isErrorHandlerEnabled) {
    return new OrdersServiceWithErrorHandler(flowAPI.httpClient, flowAPI.errorHandler, errorCodesMap);
  } else {
    return new OrdersService(flowAPI.httpClient);
  }
}
